import { useState } from "react";
import { Send } from "lucide-react";
import { Eyebrow } from "@/components/Reveal";
import { BRAND, SERVICES } from "@/data/site";

const MASSAGE_DURATIONS = ["45 minutes", "60 minutes", "90 minutes", "120 minutes"];
const FOUR_HAND_DURATIONS = ["60 minutes", "90 minutes", "120 minutes"];
const STANDARD_DURATION = ["Standard session"];
const TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM",
  "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM",
];

const durationsFor = (service) => {
  if (service.slug === "four-hand") return FOUR_HAND_DURATIONS;
  if (service.category === "massages" || service.category === "premium-massages")
    return MASSAGE_DURATIONS;
  return STANDARD_DURATION;
};

const FIELD =
  "w-full rounded-xl border border-bronze/20 bg-sand px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-bronze transition-colors duration-300";
const LABEL = "block text-[0.7rem] uppercase tracking-[0.2em] text-bronze mb-2";

export const BookingForm = () => {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState(SERVICES[0].slug);
  const service = SERVICES.find((s) => s.slug === slug) || SERVICES[0];
  const durations = durationsFor(service);
  const [duration, setDuration] = useState(durations[0]);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(TIME_SLOTS[0]);

  const onServiceChange = (newSlug) => {
    setSlug(newSlug);
    const next = SERVICES.find((s) => s.slug === newSlug);
    setDuration(durationsFor(next)[0]);
  };

  const submit = (e) => {
    e.preventDefault();
    const msg = [
      "Hi Ody Luxurious Spa, I'd like to book a session.",
      "",
      `Service: ${service.name}`,
      `Duration: ${duration}`,
      date ? `Date: ${date}` : null,
      `Time slot: ${slot}`,
      name ? `Name: ${name}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(`https://wa.me/919014729756?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className="rounded-3xl border border-bronze/15 bg-cream p-8 sm:p-10"
    >
      <Eyebrow>Book a Session</Eyebrow>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl text-ink">Reserve your hour</h2>
      <p className="mt-3 text-sm text-stone2">
        Pick your service, duration and time slot — your request opens in WhatsApp, ready to send.
      </p>
      <div className="mt-8 space-y-5">
        <div>
          <label htmlFor="bk-service" className={LABEL}>Service</label>
          <select
            id="bk-service"
            data-testid="booking-service-select"
            value={slug}
            onChange={(e) => onServiceChange(e.target.value)}
            className={FIELD}
          >
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.slug}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="bk-duration" className={LABEL}>Duration</label>
            <select
              id="bk-duration"
              data-testid="booking-duration-select"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className={FIELD}
            >
              {durations.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="bk-slot" className={LABEL}>Time slot</label>
            <select
              id="bk-slot"
              data-testid="booking-slot-select"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              className={FIELD}
            >
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="bk-date" className={LABEL}>Preferred date</label>
            <input
              id="bk-date"
              data-testid="booking-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={FIELD}
            />
          </div>
          <div>
            <label htmlFor="bk-name" className={LABEL}>Your name</label>
            <input
              id="bk-name"
              data-testid="booking-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Priya"
              className={`${FIELD} placeholder:text-stone2/50`}
            />
          </div>
        </div>
        <button
          type="submit"
          data-testid="booking-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-wa text-white px-6 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-wa-dark"
        >
          <Send size={15} /> Send Booking on WhatsApp
        </button>
        <p className="text-center text-[0.7rem] text-stone2/70 tracking-wide">
          Prefer to talk? Call {BRAND.phoneDisplay}
        </p>
      </div>
    </form>
  );
};
